import type {
  RunbookRecord,
  RunbookAuthoringProposalReview,
  RunbookAuthoringProposalDecisionResult,
} from "@bitsentry-ce/components/services";
import type { trpcClient } from "./trpc/client";
import { cacheSavedRunbook } from "./query/dashboard";

type RawRunbookAuthoringProposalReview = Awaited<
  ReturnType<typeof trpcClient.runbooksRouter.listAuthoringProposals.query>
>[number];
type RawRunbookAuthoringDecision =
  | Awaited<
      ReturnType<
        typeof trpcClient.runbooksRouter.approveAuthoringProposal.mutate
      >
    >
  | Awaited<
      ReturnType<
        typeof trpcClient.runbooksRouter.rejectAuthoringProposal.mutate
      >
    >
  | Awaited<
      ReturnType<
        typeof trpcClient.runbooksRouter.requestAuthoringRevision.mutate
      >
    >
  | Awaited<
      ReturnType<
        typeof trpcClient.runbooksRouter.restoreAuthoringProposal.mutate
      >
    >;

type RawApprovedRunbookAuthoringDecision = Awaited<
  ReturnType<typeof trpcClient.runbooksRouter.approveAuthoringProposal.mutate>
>;
type ApproveRunbookAuthoringProposalInput = Parameters<
  typeof trpcClient.runbooksRouter.approveAuthoringProposal.mutate
>[0];

export function normalizeRunbookAuthoringProposal(
  proposal: RawRunbookAuthoringProposalReview,
): RunbookAuthoringProposalReview {
  return {
    ...proposal,
    supportsOperationApproval:
      proposal.supportsOperationApproval ??
      (proposal.kind === "edit_existing_runbook" ||
        proposal.parentProposalId !== undefined),
    operationDiffs: proposal.operationDiffs.map((diff) => ({
      ...diff,
      riskLabels: [...diff.riskLabels],
      before: diff.before ?? null,
      after: diff.after ?? null,
    })),
  };
}

export function normalizeRunbookAuthoringDecision(
  result: RawRunbookAuthoringDecision,
): RunbookAuthoringProposalDecisionResult {
  return {
    ...result,
    proposal: normalizeRunbookAuthoringProposal(result.proposal),
  };
}

export async function approveRunbookAuthoringProposal(
  input: ApproveRunbookAuthoringProposalInput,
  mutate: (
    request: ApproveRunbookAuthoringProposalInput,
  ) => Promise<RawApprovedRunbookAuthoringDecision>,
  scope: string,
): Promise<RunbookAuthoringProposalDecisionResult> {
  const result = normalizeRunbookAuthoringDecision(await mutate(input));
  const savedRunbook = result.savedRunbook;
  if (savedRunbook != null) {
    await cacheSavedRunbook(scope, savedRunbook as RunbookRecord);
  }

  return result;
}
